import {
  ArrowUpRightIcon,
  ChevronLeftIcon,
  File05Icon,
  ListIcon,
  PlayCircleIcon,
  Scales01Icon,
} from '@langchain/untitled-ui-icons';

import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { emulateNativeClick } from '@/components/DataGrid.utils';
import { Skeleton } from '@/components/Skeleton';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { Badge, Button, IconButton } from '@/design-system';
import { useRunRules } from '@/hooks/useSwr';
import {
  appDatasetsPath,
  appEvaluatorPath,
  appOrganizationPath,
  appProjectsPath,
  appSessionPath,
} from '@/utils/constants';

type AnnotationQueueHeaderProps = {
  annotationQueueName: string;
  organizationId: string | null;
  index?: number;
  totalRuns?: number;
  annotationQueueId: string;

  populatingInProgress: boolean;
};

type AlignQueueHeaderProps = AnnotationQueueHeaderProps & {
  runRuleId: string;
  defaultDatasetId: string | null;
};

export const AnnotationQueueHeader = (props: AnnotationQueueHeaderProps) => {
  const navigate = useNavigate();

  const targetUrl = useMemo(
    () =>
      `/${appOrganizationPath}/${props.organizationId}/annotation-queues/${props.annotationQueueId}/list`,
    [props.annotationQueueId, props.organizationId]
  );
  return (
    <div
      role="heading"
      className="-ml-4 inline-flex h-[57px] shrink-0 items-center justify-between gap-4 self-stretch overflow-visible border-b border-secondary px-6"
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-4">
          <IconButton
            label="Back"
            icon={ChevronLeftIcon}
            variant="outlined"
            color="secondary"
            size="sm"
            onClick={() =>
              navigate(
                `/${appOrganizationPath}/${props.organizationId}/annotation-queues`
              )
            }
          />

          <div className="flex items-center gap-2">
            <TextOverflowTooltip textVariant="h3" className="max-w-[300px]">
              {props.annotationQueueName}
            </TextOverflowTooltip>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {props.populatingInProgress && (
            <div className="text-md shrink-0 text-disabled">
              Queue is being populated...
            </div>
          )}
          <Button
            variant="outlined"
            color="secondary"
            onClick={(e) => {
              const clickResult = emulateNativeClick(targetUrl, e.nativeEvent);
              if (!clickResult) {
                navigate(targetUrl);
              }
            }}
            size="md"
            leftDecorator={ListIcon}
          >
            View all runs
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AlignQueueHeader = (props: AlignQueueHeaderProps) => {
  const navigate = useNavigate();

  const { data: rules, isLoading: isLoadingRules } = useRunRules(
    props.runRuleId ? { id: [props.runRuleId] } : null
  );

  const aligningRule = rules?.[0];

  const targetUrl = useMemo(
    () =>
      `/${appOrganizationPath}/${props.organizationId}/annotation-queues/${props.annotationQueueId}/list`,
    [props.annotationQueueId, props.organizationId]
  );

  const evaluatorUrl = aligningRule?.dataset_id
    ? `/${appOrganizationPath}/${props.organizationId}/${appDatasetsPath}/${aligningRule?.dataset_id}/${appEvaluatorPath}/${props.runRuleId}`
    : `/${appOrganizationPath}/${props.organizationId}/${appProjectsPath}/${appSessionPath}/${aligningRule?.session_id}/${appEvaluatorPath}/${props.runRuleId}`;

  return (
    <div
      role="heading"
      className="-ml-4 inline-flex h-[57px] shrink-0 items-center justify-between gap-2 self-stretch overflow-visible border-b border-secondary px-4"
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <Link
            to={`/${appOrganizationPath}/${props.organizationId}/annotation-queues`}
          >
            <IconButton
              label="Back"
              icon={ChevronLeftIcon}
              variant="plain"
              color="secondary"
              size="sm"
            />
          </Link>
          <div className="flex max-w-[410px] items-center gap-2">
            <TextOverflowTooltip textVariant="h3">
              {props.annotationQueueName}
            </TextOverflowTooltip>
            {isLoadingRules ? (
              <Skeleton className="h-6"></Skeleton>
            ) : (
              <Link to={evaluatorUrl}>
                <Badge
                  size="md"
                  rounded="sm"
                  color="special"
                  leftDecorator={Scales01Icon}
                  rightDecorator={ArrowUpRightIcon}
                  className="cursor-pointer whitespace-nowrap"
                >
                  VIEW EVALUATOR
                </Badge>
              </Link>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {props.populatingInProgress && (
            <div className="text-md shrink-0 text-disabled">
              Queue is being populated...
            </div>
          )}
          <Link to={targetUrl}>
            <Button
              variant="plain"
              color="secondary"
              onClick={(e) => {
                const clickResult = emulateNativeClick(
                  targetUrl,
                  e.nativeEvent
                );
                if (!clickResult) {
                  navigate(targetUrl);
                }
              }}
              size="md"
            >
              View all runs
            </Button>
          </Link>
          <Link to={`${evaluatorUrl}/playground`}>
            <Button
              size="md"
              variant="outlined"
              color="primary"
              onClick={(e) => {
                const clickResult = emulateNativeClick(
                  targetUrl,
                  e.nativeEvent
                );
                if (!clickResult) {
                  navigate(targetUrl);
                }
              }}
              leftDecorator={PlayCircleIcon}
            >
              Evaluator playground
            </Button>
          </Link>
          <Link
            to={`https://docs.langchain.com/langsmith/improve-judge-evaluator-feedback#2-label-examples`}
            target="_blank"
          >
            <IconButton
              color="secondary"
              label="Docs"
              size="md"
              icon={File05Icon}
            />
          </Link>
        </div>
      </div>
    </div>
  );
};
